import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import {
  WasteLogCard,
  WasteLogDetailModal,
} from '@/features/waste';
import type { WasteLog } from '@/features/waste';
import { toast } from 'sonner';

interface WasteGridProps {
  readonly wasteLogs: WasteLog[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onRefetch: () => void;
}

export function WasteGrid({
  wasteLogs,
  isLoading,
  error,
  onRefetch,
}: WasteGridProps) {
  const [selectedLog, setSelectedLog] = useState<WasteLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleCardClick = (wasteLog: WasteLog) => {
    setSelectedLog(wasteLog);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedLog(null);
  };

  const handleEdit = (wasteLog: WasteLog) => {
    toast.info(`Edit functionality for "${wasteLog.itemName}" coming soon`);
    setIsDetailModalOpen(false);
  };

  const handleDeleteClick = (wasteLog: WasteLog) => {
    setSelectedLog(wasteLog);
    setIsDeleteModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (selectedLog) {
      toast.success(`Waste log for "${selectedLog.itemName}" deleted`);
      setIsDeleteModalOpen(false);
      setSelectedLog(null);
      onRefetch();
    }
  };

  const handleViewItem = (wasteLog: WasteLog) => {
    toast.info(`Navigating to item "${wasteLog.itemName}" in catalog`);
    setIsDetailModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skel-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Trash2 className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load waste logs</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (wasteLogs.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Trash2 className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No waste logs found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {wasteLogs.map((log) => (
          <WasteLogCard key={log.wasteId} wasteLog={log} onClick={handleCardClick} />
        ))}
      </div>

      <WasteLogDetailModal
        wasteLog={selectedLog}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onViewItem={handleViewItem}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Waste Log"
        message={`Are you sure you want to delete the waste log for "${selectedLog?.itemName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
